import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Building2, Search, Wallet, ClipboardList, Scale, FolderKanban, AlertTriangle,
  ArrowUpRight, CircleDot, Users, CalendarDays, Landmark, ReceiptText, Hammer,
  TrendingDown, Coins, X,
} from 'lucide-react';
import api from '../lib/api';
import { fmtMoneyLabel, fmtDate } from '../lib/format';
import { EmptyState } from '../components/common';
import { StatusPill } from '../components/StatusPill';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { cn } from '../lib/utils';

// Budget-health presentation: Green < 80%, Amber 80–100%, Red > 100% spent.
const HEALTH = {
  on_track: { label: 'On Track', tone: 'success', bar: 'bg-[hsl(var(--success))]' },
  at_risk: { label: 'At Risk', tone: 'warning', bar: 'bg-[hsl(var(--warning))]' },
  over_budget: { label: 'Over Budget', tone: 'danger', bar: 'bg-[hsl(var(--danger))]' },
};

// P7 drill-down flags: static predicates over each project row.
const FLAG_META = {
  under_billed: { label: 'Under-billed (WIP to invoice)', test: (p) => (p.wip || 0) > 0 },
  over_billed: { label: 'Over-billed', test: (p) => (p.wip || 0) < 0 },
  awarded: { label: 'Awaiting opening (AWARDED)', test: (p) => p.ccStatus === 'AWARDED' },
  erosion: { label: 'Margin erosion', test: (p) => p.hasActuals && (p.marginErosion || 0) > 0 },
};

function HealthPill({ health }) {
  const h = HEALTH[health] || HEALTH.on_track;
  return (
    <span
      data-testid={`health-${health}`}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium',
        `bg-[hsl(var(--${h.tone})/0.12)] text-[hsl(var(--${h.tone}))]`,
      )}
    >
      <CircleDot size={11} />{h.label}
    </span>
  );
}

function Kpi({ icon: Icon, label, value, tone, testid }) {
  return (
    <div className="erak-card flex items-center gap-3 p-4" data-testid={testid}>
      <span className={cn('grid h-10 w-10 shrink-0 place-items-center rounded-xl', tone || 'bg-muted/50')}>
        <Icon size={18} />
      </span>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="money text-base font-bold leading-tight tabular-nums">{value}</div>
      </div>
    </div>
  );
}

export default function BudgetProjects({ view = 'project' }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [dq, setDq] = useState(''); // debounced query (D3)
  const [searchParams, setSearchParams] = useSearchParams();
  const [healthFilter, setHealthFilter] = useState(searchParams.get('health') || 'all');
  // P7 drill-down flag from the cockpit: under_billed | over_billed | awarded | erosion
  const [flag, setFlag] = useState(searchParams.get('flag') || 'all');
  const navigate = useNavigate();

  const isCosting = view === 'costing';
  const basePath = isCosting ? '/costing' : '/budget';
  const heading = isCosting ? 'Cost Centre Portfolio' : 'Project Tracker';
  const subtitle = isCosting
    ? 'Every cost centre at a glance — contract value, committed cost, SAP actuals, WIP and projected margin. Open cost centres, book cost, capture savings and manage revisions.'
    : 'Won projects with a frozen budget baseline — monitor site execution and delivery health.';

  useEffect(() => {
    // Project module lists only OPEN (cost-centre-approved) projects; Costing shows all.
    const url = isCosting ? '/budget/projects' : '/budget/projects?module=project';
    api.get(url).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [isCosting]);

  // D3 — debounce the search so filtering doesn't run on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDq(q), 200);
    return () => clearTimeout(t);
  }, [q]);

  // Currency for the KPI strip — projects share one currency in practice; fall back to AED.
  const cur = (rows && rows[0] && rows[0].currency) || 'AED';

  const clearFlag = () => {
    setFlag('all');
    const sp = new URLSearchParams(searchParams);
    sp.delete('flag'); sp.delete('health');
    setSearchParams(sp, { replace: true });
    setHealthFilter('all');
  };

  const filtered = useMemo(() => (rows || []).filter((p) => {
    if (healthFilter !== 'all' && p.health !== healthFilter) return false;
    if (flag !== 'all' && FLAG_META[flag] && !FLAG_META[flag].test(p)) return false;
    return `${p.code} ${p.name} ${p.client}`.toLowerCase().includes(dq.toLowerCase());
  }), [rows, dq, healthFilter, flag]);

  const kpis = useMemo(() => {
    const src = rows || [];
    return {
      count: src.length,
      budget: src.reduce((s, p) => s + (p.budget || 0), 0),
      booked: src.reduce((s, p) => s + (p.booked || 0), 0),
      balance: src.reduce((s, p) => s + (p.balance || 0), 0),
      over: src.filter((p) => p.overBudget).length,
    };
  }, [rows]);

  // Part 6 — portfolio roll-ups (exact sums of per-project figures to the fil).
  const portfolio = useMemo(() => {
    const src = rows || [];
    return {
      count: src.length,
      contractValue: src.reduce((s, p) => s + (p.contractValue || 0), 0),
      budget: src.reduce((s, p) => s + (p.budget || 0), 0),
      committed: src.reduce((s, p) => s + (p.committed ?? p.booked ?? 0), 0),
      actual: src.reduce((s, p) => s + (p.actualTotal || 0), 0),
      invoiced: src.reduce((s, p) => s + (p.cumulativeInvoiced || 0), 0),
      wip: src.reduce((s, p) => s + (p.wip || 0), 0),
      marginErosion: src.reduce((s, p) => s + (p.marginErosion || 0), 0),
      over: src.filter((p) => p.overBudget).length,
    };
  }, [rows]);

  return (
    <div className="space-y-6 p-4 md:p-6 lg:p-8" data-testid="project-tracker">
      <div>
        <h1 className="font-display text-2xl font-bold md:text-3xl">{heading}</h1>
        <p className="text-sm text-muted-foreground">
          {subtitle}
        </p>
      </div>

      {/* KPI summary strip */}
      {rows === null ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-[72px] animate-pulse rounded-2xl bg-muted/40" />)}
        </div>
      ) : isCosting ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="portfolio-kpis">
          <Kpi testid="kpi-projects" icon={FolderKanban} label="Cost Centres" value={portfolio.count}
            tone="bg-[hsl(var(--brand)/0.12)] text-[hsl(var(--brand))]" />
          <Kpi testid="kpi-contract-value" icon={Landmark} label={`Total Contract Value (${cur})`} value={fmtMoneyLabel(portfolio.contractValue, cur, 0)}
            tone="bg-[hsl(var(--brand)/0.12)] text-[hsl(var(--brand))]" />
          <Kpi testid="kpi-budget" icon={Wallet} label={`Total Budget (${cur})`} value={fmtMoneyLabel(portfolio.budget, cur, 0)}
            tone="bg-[hsl(var(--teal)/0.12)] text-[hsl(var(--teal))]" />
          <Kpi testid="kpi-committed" icon={ClipboardList} label={`Total Committed (${cur})`} value={fmtMoneyLabel(portfolio.committed, cur, 0)}
            tone="bg-[hsl(var(--copper)/0.12)] text-[hsl(var(--copper))]" />
          <Kpi testid="kpi-actual" icon={Coins} label={`Total Actual (${cur})`} value={fmtMoneyLabel(portfolio.actual, cur, 0)}
            tone="bg-[hsl(var(--copper)/0.12)] text-[hsl(var(--copper))]" />
          <Kpi testid="kpi-invoiced" icon={ReceiptText} label={`Total Cumulative Invoiced (${cur})`} value={fmtMoneyLabel(portfolio.invoiced, cur, 0)}
            tone="bg-[hsl(var(--teal)/0.12)] text-[hsl(var(--teal))]" />
          <Kpi testid="kpi-wip" icon={Hammer} label={`Total WIP (${cur})`} value={fmtMoneyLabel(portfolio.wip, cur, 0)}
            tone={portfolio.wip < 0 ? 'bg-[hsl(var(--danger)/0.12)] text-[hsl(var(--danger))]' : 'bg-[hsl(var(--warning)/0.12)] text-[hsl(var(--warning))]'} />
          <Kpi testid="kpi-margin-erosion" icon={TrendingDown} label={`Margin Erosion (${cur})`} value={fmtMoneyLabel(portfolio.marginErosion, cur, 0)}
            tone={portfolio.marginErosion > 0 ? 'bg-[hsl(var(--danger)/0.12)] text-[hsl(var(--danger))]' : 'bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))]'} />
          <Kpi testid="kpi-over-budget" icon={AlertTriangle} label="Over Budget" value={portfolio.over}
            tone={portfolio.over > 0 ? 'bg-[hsl(var(--danger)/0.12)] text-[hsl(var(--danger))]' : 'bg-muted/50 text-muted-foreground'} />
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <Kpi testid="kpi-projects" icon={FolderKanban} label="Projects" value={kpis.count}
            tone="bg-[hsl(var(--brand)/0.12)] text-[hsl(var(--brand))]" />
          <Kpi testid="kpi-budget" icon={Wallet} label={`Total Budget (${cur})`} value={fmtMoneyLabel(kpis.budget, cur, 0)}
            tone="bg-[hsl(var(--teal)/0.12)] text-[hsl(var(--teal))]" />
          <Kpi testid="kpi-booked" icon={ClipboardList} label={`Total Booked (${cur})`} value={fmtMoneyLabel(kpis.booked, cur, 0)}
            tone="bg-[hsl(var(--copper)/0.12)] text-[hsl(var(--copper))]" />
          <Kpi testid="kpi-balance" icon={Scale} label={`Total Balance (${cur})`} value={fmtMoneyLabel(kpis.balance, cur, 0)}
            tone={kpis.balance < 0 ? 'bg-[hsl(var(--danger)/0.12)] text-[hsl(var(--danger))]' : 'bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))]'} />
          <Kpi testid="kpi-over-budget" icon={AlertTriangle} label="Over Budget" value={kpis.over}
            tone={kpis.over > 0 ? 'bg-[hsl(var(--danger)/0.12)] text-[hsl(var(--danger))]' : 'bg-muted/50 text-muted-foreground'} />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative max-w-xs flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="tracker-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search code, name or client…" className="pl-9" />
        </div>
        <div className="flex items-center gap-1">
          {[
            { k: 'all', label: 'All' },
            { k: 'on_track', label: 'On Track' },
            { k: 'at_risk', label: 'At Risk' },
            { k: 'over_budget', label: 'Over Budget' },
          ].map((f) => (
            <button key={f.k} data-testid={`tracker-filter-${f.k}`} onClick={() => setHealthFilter(f.k)}
              className={cn('rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
                healthFilter === f.k ? 'bg-[hsl(var(--brand))] text-white' : 'bg-muted/50 text-muted-foreground hover:bg-muted')}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {flag !== 'all' && FLAG_META[flag] && (
        <div className="flex items-center gap-2" data-testid="tracker-active-flag">
          <Badge variant="outline" className="gap-1.5 border-[hsl(var(--brand)/0.4)] py-1 text-xs text-[hsl(var(--brand))]">
            <Hammer size={12} />Filtered: {FLAG_META[flag].label} · {filtered.length}
            <button onClick={clearFlag} data-testid="tracker-clear-flag" className="ml-1 rounded-full p-0.5 hover:bg-[hsl(var(--brand)/0.12)]"><X size={12} /></button>
          </Badge>
        </div>
      )}

      {rows === null ? (
        <div className="h-64 animate-pulse rounded-2xl bg-muted/40" />
      ) : filtered.length === 0 ? (
        <EmptyState testid="tracker-empty" icon={Building2}
          title={(rows || []).length === 0 ? (isCosting ? 'No won projects yet' : 'No open cost centres yet') : 'No matching projects'}
          description={(rows || []).length === 0
            ? (isCosting
              ? 'When a tender is marked Won, its baseline freezes and it appears here for budget tracking.'
              : 'Projects appear here once Costing opens the cost centre (AWARDED → OPEN). Until then, they stay in the Costing module.')
            : 'Try clearing the search or budget-health filter.'} />
      ) : (
        <div className="erak-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="tracker-table">
              <thead>
                <tr className="border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">Project</th>
                  <th className="px-4 py-3 font-semibold">Team</th>
                  <th className="px-4 py-3 font-semibold">Duration</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  {isCosting && <th className="px-4 py-3 text-right font-semibold">Contract Value</th>}
                  <th className="px-4 py-3 text-right font-semibold">Budget</th>
                  <th className="px-4 py-3 text-right font-semibold">Booked</th>
                  <th className="px-4 py-3 text-right font-semibold">Balance</th>
                  {isCosting && <th className="px-4 py-3 text-right font-semibold">Actual (SAP)</th>}
                  {isCosting && <th className="px-4 py-3 text-right font-semibold">WIP</th>}
                  {isCosting && <th className="px-4 py-3 text-right font-semibold">Margin</th>}
                  <th className="px-4 py-3 font-semibold">% Spent</th>
                  <th className="px-4 py-3 font-semibold">Health</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const pct = p.pctSpent || 0;
                  const engNames = p.assignedEngineerNames || [];
                  return (
                    <tr key={p.id} data-testid={`tracker-row-${p.id}`} onClick={() => navigate(`${basePath}/${p.id}`)}
                      className="group cursor-pointer border-b last:border-0 hover:bg-muted/40">
                      <td className="px-4 py-3">
                        <div className="font-num text-xs text-[hsl(var(--copper))]">{p.code}</div>
                        <div className="font-medium">{p.name}</div>
                        <div className="text-xs text-muted-foreground">{p.client || '—'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 text-xs">
                          <Users size={12} className="text-muted-foreground" />
                          <span className="font-medium">{p.projectManagerName || 'No PM'}</span>
                        </div>
                        <div className="mt-0.5 text-[11px] text-muted-foreground">
                          {engNames.length === 0 ? 'No engineers'
                            : engNames.length <= 2 ? engNames.join(', ')
                              : `${engNames.slice(0, 2).join(', ')} +${engNames.length - 2}`}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {p.startDate || p.endDate ? (
                          <div className="flex items-center gap-1">
                            <CalendarDays size={12} />
                            <span>{p.startDate ? fmtDate(p.startDate) : '—'} → {p.endDate ? fmtDate(p.endDate) : '—'}</span>
                          </div>
                        ) : <span className="text-muted-foreground/70">Not set</span>}
                      </td>
                      <td className="px-4 py-3"><StatusPill status={p.status} /></td>
                      {isCosting && <td className="px-4 py-3 text-right money" data-testid={`row-contract-value-${p.id}`}>{fmtMoneyLabel(p.contractValue, p.currency)}</td>}
                      <td className="px-4 py-3 text-right money">{fmtMoneyLabel(p.budget, p.currency)}</td>
                      <td className="px-4 py-3 text-right money">{fmtMoneyLabel(p.booked, p.currency)}</td>
                      <td className={cn('px-4 py-3 text-right money font-medium', p.overBudget && 'text-[hsl(var(--danger))]')}>
                        {fmtMoneyLabel(p.balance, p.currency)}
                      </td>
                      {isCosting && (
                        <td className="px-4 py-3 text-right money" data-testid={`row-actual-${p.id}`}>
                          {p.hasActuals ? fmtMoneyLabel(p.actualTotal, p.currency) : <span className="text-xs text-muted-foreground/70">No actuals</span>}
                        </td>
                      )}
                      {isCosting && (
                        <td className={cn('px-4 py-3 text-right money font-medium', p.wip > 0 ? 'text-[hsl(var(--warning))]' : p.wip < 0 ? 'text-[hsl(var(--danger))]' : '')}
                          data-testid={`row-wip-${p.id}`}
                          title={p.wip > 0 ? 'Under-billed' : p.wip < 0 ? 'Over-billed' : 'Fully billed'}>
                          {p.wip > 0 ? '+' : p.wip < 0 ? '−' : ''}{fmtMoneyLabel(Math.abs(p.wip || 0), p.currency)}
                        </td>
                      )}
                      {isCosting && (
                        <td className="px-4 py-3 text-right money" data-testid={`row-margin-${p.id}`}>
                          {p.hasActuals && p.projectedMargin != null ? (
                            <div>
                              <div className={cn('font-medium', p.projectedMargin < 0 && 'text-[hsl(var(--danger))]')}>{fmtMoneyLabel(p.projectedMargin, p.currency)}</div>
                              <div className="text-[11px] text-muted-foreground tabular-nums">{p.projectedMarginPct}%</div>
                            </div>
                          ) : <span className="text-xs text-muted-foreground/70">POC n/a</span>}
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
                            <div className={cn('h-full rounded-full', (HEALTH[p.health] || HEALTH.on_track).bar)}
                              style={{ width: `${Math.min(pct, 100)}%` }} />
                          </div>
                          <span className="font-num text-xs tabular-nums">{pct}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3"><HealthPill health={p.health} /></td>
                      <td className="px-4 py-3 text-right">
                        <ArrowUpRight size={16} className="ml-auto text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
